import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function AuthPage({ mode }) {
  const isSignup = mode === "signup";
  const { signup, login } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [magicBusy, setMagicBusy] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isSignup) await signup(email, password);
      else await login(email, password);
      const next = params.get("next");
      if (next === "buy") nav("/?buy=1");
      else nav("/continue");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const sendMagicLink = async () => {
    if (!email) { toast.error("enter your email first"); return; }
    setMagicBusy(true);
    try {
      await api.post("/auth/magic-link/request", { email });
      setMagicSent(true);
      toast.success("check your email");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "could not send the link");
    } finally {
      setMagicBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12" data-testid="auth-page">
      <div className="w-full max-w-md">
        <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: "var(--gold)" }}>
          {isSignup ? "create an account" : "welcome back"}
        </p>
        <h1 className="font-serif text-4xl mb-10" style={{ color: "var(--ink)" }}>
          {isSignup ? "get access" : "sign in"}
        </h1>
        <form onSubmit={submit} className="space-y-6" data-testid="auth-form">
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>email</label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="field" data-testid="auth-email"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>password</label>
            <input
              type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="field" data-testid="auth-password"
            />
          </div>
          <button disabled={busy} className="btn-primary w-full" data-testid="auth-submit">
            {busy ? "..." : isSignup ? "create account" : "sign in"}
          </button>
        </form>

        {!isSignup && (
          <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--gold-soft)" }} data-testid="magic-link-block">
            {magicSent ? (
              <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
                if that email has an account, a sign-in link is on its way. it expires in 20 minutes.
              </p>
            ) : (
              <>
                <p className="text-xs uppercase tracking-widest mb-3" style={{ color: "var(--gold)" }}>
                  no password?
                </p>
                <p className="text-sm mb-3" style={{ color: "var(--ink-soft)" }}>
                  bought the course but never set one? we&apos;ll email you a one-tap sign-in link.
                </p>
                <button
                  type="button"
                  onClick={sendMagicLink}
                  disabled={magicBusy || !email}
                  className="btn-ghost w-full"
                  data-testid="magic-link-btn"
                >
                  {magicBusy ? "sending..." : "email me a sign-in link"}
                </button>
              </>
            )}
          </div>
        )}

        <p className="text-sm mt-6" style={{ color: "var(--ink-soft)" }}>
          {isSignup ? (
            <>already have an account? <Link to="/login" className="underline" data-testid="link-login">sign in</Link></>
          ) : (
            <>new here? <Link to="/signup" className="underline" data-testid="link-signup">create an account</Link></>
          )}
        </p>
      </div>
    </div>
  );
}
